import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/@core/services/alert.service';
import {
  RuleBuilderCondition,
  RuleBuilderFieldOption,
  RuleBuilderOption,
  RuleBuilderWizardPageOption,
  RuleBuilderPanelOption,
  RuleBuilderTabOption,
  SupportingDocumentRuleRecord
} from './supporting-document-rule-config.models';
import { SupportingDocumentRuleConfigService } from './supporting-document-rule-config.service';
import { SupportingDocumentGridOption } from '../supporting-document-grid/supporting-document-grid-config.model';

@Component({
  standalone: false,
  selector: 'app-supporting-document-rule-config',
  templateUrl: './supporting-document-rule-config.component.html',
  styleUrls: ['./supporting-document-rule-config.component.scss']
})
export class SupportingDocumentRuleConfigComponent implements OnInit {
  static readonly ALL_CATEGORIES_VALUE = 'ALL';
  ruleForm: FormGroup;
  wizardPageOptions: RuleBuilderWizardPageOption[] = [];
  tabOptions: RuleBuilderTabOption[] = [];
  panelOptions: RuleBuilderPanelOption[] = [];
  fieldOptions: RuleBuilderFieldOption[] = [];
  categoryOptions: RuleBuilderOption[] = [];
  documentTypeOptions: RuleBuilderOption[] = [];
  rules: SupportingDocumentRuleRecord[] = [];
  editingRuleId: number | null = null;
  previewConditionJson = '';
  readonly allCategoriesValue = SupportingDocumentRuleConfigComponent.ALL_CATEGORIES_VALUE;
  private supportingDocumentCategories: SupportingDocumentGridOption[] = [];

  operatorOptions: RuleBuilderOption[] = [
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'notEquals' },
    { label: 'Contains', value: 'contains' },
    { label: 'Is True', value: 'isTrue' },
    { label: 'Is False', value: 'isFalse' },
    { label: 'Is Not Empty', value: 'isNotEmpty' }
  ];

  private panelsByTab: Record<string, RuleBuilderPanelOption[]> = {};
  private fieldsByPanel: Record<string, RuleBuilderFieldOption[]> = {};
  private pageMetadataByStepperId: Record<string, RuleBuilderWizardPageOption> = {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly alertService: AlertService,
    private readonly ruleConfigService: SupportingDocumentRuleConfigService
  ) {
    this.ruleForm = this.fb.group({
      stepperId: ['', Validators.required],
      tabId: ['', Validators.required],
      sectionKey: ['', Validators.required],
      logicalOperator: ['AND', Validators.required],
      conditions: this.fb.array([this.createConditionFormGroup()]),
      category: [''],
      documentType: [''],
      conditionJson: [''],
      sortOrder: [1, Validators.required],
      isActive: [true],
      showAlwaysDefault: [false],
      showAlwaysRoleId: [''],
      showAlwaysRoleMatch: [false],
      isNotRequired: [false],
      showAdditionalCategory: [false],
      enableDynamicTypeFromField: [false],
      dynamicTypeSourceField: [''],
      dynamicTypeLookupCategory: ['']
    });
  }

  ngOnInit(): void {
    this.loadPages();
    this.loadLookups();
    this.loadRules();
  }

  onCategoryChange(): void {
    const selectedCategory = this.ruleForm.get('category')?.value || '';
    if (selectedCategory === this.allCategoriesValue) {
      // "All Categories" means the runtime grid will let the user pick any
      // category for the current section, so a specific document Type cannot
      // be pre-selected here.
      this.documentTypeOptions = [];
      this.ruleForm.patchValue({ documentType: '' });
      return;
    }
    this.syncDocumentTypeOptions();
  }

  onShowAdditionalCategoryChange(): void {
    this.refreshCategoryOptionsForSection(true);
    this.syncDocumentTypeOptions();
  }

  onStepperPageChange(): void {
    const stepperId = this.ruleForm.get('stepperId')?.value;
    this.ruleForm.patchValue({
      tabId: '',
      sectionKey: ''
    });
    this.tabOptions = [];
    this.panelOptions = [];
    this.fieldOptions = [];
    if (!stepperId) {
      return;
    }

    const selectedPage = this.pageMetadataByStepperId[stepperId];
    const stepperVersionId = selectedPage?.stepperVersionId || stepperId;
    this.ruleConfigService.getPageMetadataForDocument(stepperVersionId).subscribe({
      next: (metadata) => {
        this.tabOptions = metadata.tabs.length ? metadata.tabs : [{ pageName: 'Default', pageId: 'default' }];
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Unable to load page metadata.');
      }
    });
  }

  onTabChange(preserveSection = false): void {
    const tabKey = this.ruleForm.get('tabId')?.value || 'default';
    if (!preserveSection) {
      this.ruleForm.patchValue({
        sectionKey: ''
      });
    }
    this.ruleConfigService.getPageMetadataForTabPage(tabKey).subscribe({
      next: (metadata) => {
        this.panelOptions = metadata.sections.length ? metadata.sections : [{ sectionNameLabel: 'Default', sectionNameKey: 'default', sectionFields: [] }];
        this.syncFieldOptionsForSelectedSection();
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Unable to load page metadata.');
      }
    });
    this.fieldOptions = [];
  }

  onSectionChange(): void {
    this.syncFieldOptionsForSelectedSection();
    this.refreshCategoryOptionsForSection(true);
    this.syncExpressions();
  }

  syncExpressions(): void {
    const conditionJson = this.ruleConfigService.buildConditionGroup(this.conditionRows.getRawValue(), this.ruleForm.get('logicalOperator')?.value);
    if (!conditionJson) {
      this.previewConditionJson = '';
      this.ruleForm.patchValue({ conditionJson: '' }, { emitEvent: false });
      return;
    }

    this.previewConditionJson = JSON.stringify(conditionJson, null, 2);
    this.ruleForm.patchValue({ conditionJson: this.previewConditionJson }, { emitEvent: false });
  }

  saveRule(): void {
    this.ruleForm.markAllAsTouched();
    if (this.ruleForm.invalid) {
      this.alertService.error('Please complete the required rule fields.');
      return;
    }

    const wizardPage = this.wizardPageOptions.find((item: RuleBuilderWizardPageOption) => item.stepperId === this.ruleForm.value.stepperId);
    const tab = this.tabOptions.find((item: RuleBuilderTabOption) => item.pageId === this.ruleForm.value.tabId);

    let conditionJson: any = null;
    if (this.ruleForm.value.conditionJson) {
      try {
        conditionJson = JSON.parse(this.ruleForm.value.conditionJson);
      } catch (error) {
        console.error(error);
        this.alertService.error('Condition JSON is not valid.');
        return;
      }
    }

    // Embed is_not_required flag inside condition_json since there's no separate DB column
    if (!!this.ruleForm.value.isNotRequired) {
      if (conditionJson && typeof conditionJson === 'object') {
        conditionJson.is_not_required = true;
      } else {
        conditionJson = { is_not_required: true };
      }
    } else if (conditionJson && typeof conditionJson === 'object') {
      delete conditionJson.is_not_required;
    }

    // Embed show_always role restriction inside condition_json
    const showAlwaysRoleId = String(this.ruleForm.value.showAlwaysRoleId || '').trim();
    if (!!this.ruleForm.value.showAlwaysDefault && showAlwaysRoleId) {
      conditionJson = conditionJson && typeof conditionJson === 'object' ? conditionJson : {};
      conditionJson.show_always_role_id = showAlwaysRoleId;
      conditionJson.show_always_role_match = this.ruleForm.value.showAlwaysRoleMatch === true || this.ruleForm.value.showAlwaysRoleMatch === 'true';
    } else if (conditionJson && typeof conditionJson === 'object') {
      delete conditionJson.show_always_role_id;
      delete conditionJson.show_always_role_match;
    }

    const payload: SupportingDocumentRuleRecord = {
      rule_id: this.editingRuleId,
      stepper_id: this.ruleForm.value.stepperId,
      stepper_name: wizardPage?.wizardPageName || '',
      page_id: this.ruleForm.value.tabId,
      page_name: tab?.pageName || '',
      panel_key: this.ruleForm.value.sectionKey,
      cd_document_category: this.ruleForm.value.category || '',
      cd_document_type: this.ruleForm.value.documentType,
      condition_json: conditionJson,
      sort_order: Number(this.ruleForm.value.sortOrder || 1),
      is_active: !!this.ruleForm.value.isActive,
      show_always_default: !!this.ruleForm.value.showAlwaysDefault,
      show_additional_category: !!this.ruleForm.value.showAdditionalCategory,
      showAdditionalCategory: !!this.ruleForm.value.showAdditionalCategory,
      dynamic_type_source_field: this.ruleForm.value.dynamicTypeSourceField || null,
      dynamic_type_lookup_category: this.ruleForm.value.dynamicTypeLookupCategory || null
    };

    const request$ = this.editingRuleId
      ? this.ruleConfigService.updateRule(this.editingRuleId, payload)
      : this.ruleConfigService.createRule(payload);

    request$.subscribe({
      next: () => {
        this.alertService.success(this.editingRuleId ? 'Rule updated successfully.' : 'Rule saved successfully.');
        this.resetForm();
        this.loadRules();
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Unable to save the rule.');
      }
    });
  }

  editRule(rule: SupportingDocumentRuleRecord): void {
    this.editingRuleId = Number(rule.rule_id || 0) || null;

    // Patch non-async fields immediately
    this.ruleForm.patchValue({
      stepperId: rule.stepper_id,
      logicalOperator: 'AND',
      conditionJson: JSON.stringify(rule.condition_json, null, 2),
      sortOrder: rule.sort_order,
      isActive: rule.is_active,
      showAlwaysDefault: !!rule.show_always_default,
      showAlwaysRoleId: rule.condition_json?.show_always_role_id || '',
      showAlwaysRoleMatch: rule.condition_json?.show_always_role_match !== undefined
        ? (rule.condition_json.show_always_role_match === true || rule.condition_json.show_always_role_match === 'true')
        : false,
      isNotRequired: !!(rule.condition_json?.is_not_required),
      showAdditionalCategory: this.coerceBoolean(rule.show_additional_category ?? (rule as any).showAdditionalCategory),
      enableDynamicTypeFromField: !!rule.dynamic_type_source_field,
      dynamicTypeSourceField: rule.dynamic_type_source_field || '',
      dynamicTypeLookupCategory: rule.dynamic_type_lookup_category || ''
    });
    this.previewConditionJson = JSON.stringify(rule.condition_json, null, 2);
    this.populateConditionsFromJson(rule.condition_json);
    this.syncDocumentTypeOptions(false);

    // Load stepper page tabs, then chain tab sections loading
    const selectedPage = this.pageMetadataByStepperId[rule.stepper_id];
    const stepperVersionId = selectedPage?.stepperVersionId || rule.stepper_id;

    this.ruleConfigService.getPageMetadataForDocument(stepperVersionId).subscribe({
      next: (metadata) => {
        this.tabOptions = metadata.tabs.length ? metadata.tabs : [{ pageName: 'Default', pageId: 'default' }];
        this.ruleForm.patchValue({ tabId: rule.page_id });

        // Now load sections for the tab
        const tabKey = rule.page_id || 'default';
        this.ruleConfigService.getPageMetadataForTabPage(tabKey).subscribe({
          next: (tabMetadata) => {
            this.panelOptions = tabMetadata.sections.length
              ? tabMetadata.sections
              : [{ sectionNameLabel: 'Default', sectionNameKey: 'default', sectionFields: [] }];

            this.ruleForm.patchValue({ sectionKey: rule.panel_key });
            this.syncFieldOptionsForSelectedSection();
            this.refreshCategoryOptionsForSection(false);

            // Ensure the rule's category is present in options even if
            // section-based filtering excluded it (timing / data mismatch).
            const ruleCategory = String(rule.cd_document_category || '').trim();
            if (ruleCategory && ruleCategory !== this.allCategoriesValue) {
              const alreadyInOptions = this.categoryOptions.some((opt: RuleBuilderOption) => opt.value === ruleCategory);
              if (!alreadyInOptions) {
                const catMeta = this.supportingDocumentCategories.find((c: SupportingDocumentGridOption) => c.key === ruleCategory);
                this.categoryOptions = [
                  ...this.categoryOptions,
                  { label: catMeta?.value || ruleCategory, value: ruleCategory }
                ];
              }
            }

            // Patch category and type AFTER options are loaded
            this.ruleForm.patchValue({
              category: rule.cd_document_category,
              documentType: rule.cd_document_type || '',
              showAdditionalCategory: this.coerceBoolean(rule.show_additional_category ?? (rule as any).showAdditionalCategory),
              enableDynamicTypeFromField: !!rule.dynamic_type_source_field,
              dynamicTypeSourceField: rule.dynamic_type_source_field || '',
              dynamicTypeLookupCategory: rule.dynamic_type_lookup_category || ''
            });
            this.syncDocumentTypeOptions(false);

            // Ensure the rule's document type is in the type options
            const ruleType = String(rule.cd_document_type || '').trim();
            if (ruleType && !this.documentTypeOptions.some((opt: RuleBuilderOption) => opt.value === ruleType)) {
              const allTypes = this.supportingDocumentCategories
                .flatMap((c: SupportingDocumentGridOption) => c.lookup || [])
                .map((t: SupportingDocumentGridOption) => ({ label: t.value, value: t.key }));
              const typeMeta = allTypes.find((t: RuleBuilderOption) => t.value === ruleType);
              this.documentTypeOptions = [
                ...this.documentTypeOptions,
                { label: typeMeta?.label || ruleType, value: ruleType }
              ];
            }
          },
          error: (error) => {
            console.error('Failed to load tab sections for edit', error);
          }
        });
      },
      error: (error) => {
        console.error('Failed to load page metadata for edit', error);
      }
    });
  }

  deleteRule(rule: SupportingDocumentRuleRecord): void {
    if (!rule.rule_id) {
      return;
    }
    this.ruleConfigService.deleteRule(rule.rule_id).subscribe({
      next: () => {
        this.alertService.success('Rule deleted successfully.');
        this.loadRules();
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Unable to delete the rule.');
      }
    });
  }

  resetForm(): void {
    this.editingRuleId = null;
    this.previewConditionJson = '';
    this.ruleForm.reset({
      logicalOperator: 'AND',
      sortOrder: 1,
      isActive: true,
      showAlwaysDefault: false,
      showAlwaysRoleId: '',
      showAlwaysRoleMatch: false,
      showAdditionalCategory: false,
      enableDynamicTypeFromField: false,
      dynamicTypeSourceField: '',
      dynamicTypeLookupCategory: ''
    });
    this.replaceConditions([]);
    this.tabOptions = [];
    this.panelOptions = [];
    this.fieldOptions = [];
    this.documentTypeOptions = [];
  }

  get conditionRows(): FormArray {
    return this.ruleForm.get('conditions') as FormArray;
  }

  addCondition(): void {
    this.conditionRows.push(this.createConditionFormGroup());
    this.syncExpressions();
  }

  removeCondition(index: number): void {
    if (this.conditionRows.length === 1) {
      this.conditionRows.at(0).patchValue({
        fieldKey: '',
        operator: 'equals',
        fieldValue: ''
      });
    } else {
      this.conditionRows.removeAt(index);
    }
    this.syncExpressions();
  }

  loadRules(): void {
    this.ruleConfigService.listRules().subscribe({
      next: (rules: SupportingDocumentRuleRecord[]) => {
        this.rules = rules;
      },
      error: (error) => {
        console.error(error);
        this.rules = [];
      }
    });
  }

  private loadPages(): void {
    this.ruleConfigService.getPages().subscribe({
      next: (pages: RuleBuilderWizardPageOption[]) => {
        this.wizardPageOptions = pages;
        this.pageMetadataByStepperId = pages.reduce((acc: Record<string, RuleBuilderWizardPageOption>, item: RuleBuilderWizardPageOption) => {
          acc[item.stepperId] = item;
          return acc;
        }, {});
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Unable to load pages.');
      }
    });
  }

  private loadLookups(): void {
    this.ruleConfigService.getSupportingDocumentCategoriesWithTypes().subscribe({
      next: (categories: SupportingDocumentGridOption[]) => {
        this.supportingDocumentCategories = categories || [];
        this.refreshCategoryOptionsForSection(false);
        this.syncDocumentTypeOptions(false);
      },
      error: (supportingLookupError) => {
        console.error(supportingLookupError);
        this.ruleConfigService.getDocumentLookupData().subscribe({
          next: (response) => {
            this.categoryOptions = response.categories;
            this.documentTypeOptions = response.documentTypes;
          },
          error: (error) => {
            console.error(error);
            this.alertService.error('Unable to load category/type lookups.');
          }
        });
      }
    });
  }

  getCategoryLabel(value: string): string {
    if (value === this.allCategoriesValue) {
      return 'All Categories';
    }
    const match = this.categoryOptions.find((item: RuleBuilderOption) => item.value === value);
    if (match?.label) {
      return match.label;
    }
    const fallback = this.supportingDocumentCategories.find(
      (item: SupportingDocumentGridOption) => item.key === value
    );
    return fallback?.value || value || '-';
  }

  isRuleAdditionalCategory(rule: SupportingDocumentRuleRecord): boolean {
    return this.coerceBoolean(rule?.show_additional_category ?? rule?.showAdditionalCategory);
  }

  private refreshCategoryOptionsForSection(resetSelection: boolean): void {
    const sectionKey = String(this.ruleForm.get('sectionKey')?.value || '').trim();
    const normalizedSection = sectionKey.toLowerCase();
    const useAdditionalSection = !!this.ruleForm.get('showAdditionalCategory')?.value;

    const filteredCategories = !normalizedSection
      ? this.supportingDocumentCategories
      : this.supportingDocumentCategories.filter((item: SupportingDocumentGridOption) => {
          const sectionName = String(item?.optionaldata?.section_name || item?.optionaldata?.sectionName || '').trim().toLowerCase();
          const additionalSection = String(item?.optionaldata?.additional_section || item?.optionaldata?.additionalSection || '').trim().toLowerCase();
          return sectionName === normalizedSection || (useAdditionalSection && additionalSection === normalizedSection);
        });

    const baseOptions: RuleBuilderOption[] = filteredCategories.map((item: SupportingDocumentGridOption) => ({
      label: item.value,
      value: item.key
    }));

    // Always expose the "All Categories" sentinel so the rule can target every
    // category that belongs to the selected section at runtime.
    this.categoryOptions = [
      { label: 'All Categories', value: this.allCategoriesValue },
      ...baseOptions
    ];

    if (!resetSelection) {
      return;
    }

    const currentCategory = this.ruleForm.get('category')?.value || '';
    const stillValid = this.categoryOptions.some((option: RuleBuilderOption) => option.value === currentCategory);
    if (!stillValid) {
      this.ruleForm.patchValue({ category: '', documentType: '' });
      this.documentTypeOptions = [];
    } else if (currentCategory === this.allCategoriesValue) {
      this.documentTypeOptions = [];
      this.ruleForm.patchValue({ documentType: '' });
    }
  }

  private coerceBoolean(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return ['true', '1', 'yes', 'y'].includes(String(value ?? '').trim().toLowerCase());
  }

  getTypeLabel(value: string, categoryValue?: string): string {
    const types = this.getTypesForCategory(categoryValue || '');
    const match = types.find((item: RuleBuilderOption) => item.value === value)
      || this.supportingDocumentCategories
        .flatMap((item: SupportingDocumentGridOption) => item.lookup || [])
        .map((item: SupportingDocumentGridOption) => ({
          label: item.value,
          value: item.key
        }))
        .find((item: RuleBuilderOption) => item.value === value);
    return match?.label || value || '-';
  }

  private syncDocumentTypeOptions(resetSelection = true): void {
    const selectedCategory = this.ruleForm.get('category')?.value || '';
    this.documentTypeOptions = this.getTypesForCategory(selectedCategory);

    const selectedDocumentType = this.ruleForm.get('documentType')?.value || '';
    const hasSelectedType = this.documentTypeOptions.some((item: RuleBuilderOption) => item.value === selectedDocumentType);
    if (resetSelection && selectedDocumentType && !hasSelectedType) {
      this.ruleForm.patchValue({ documentType: '' });
    }
  }

  private getTypesForCategory(categoryKey: string): RuleBuilderOption[] {
    const category = this.supportingDocumentCategories.find((item: SupportingDocumentGridOption) => item.key === categoryKey);
    return (category?.lookup || []).map((item: SupportingDocumentGridOption) => ({
      label: item.value,
      value: item.key
    }));
  }

  private createConditionFormGroup(condition?: Partial<RuleBuilderCondition>): FormGroup {
    return this.fb.group({
      fieldKey: [condition?.field || ''],
      operator: [condition?.operator || 'equals', Validators.required],
      fieldValue: [condition?.value ?? '']
    });
  }

  private replaceConditions(conditions: RuleBuilderCondition[]): void {
    const conditionArray = this.fb.array(
      (conditions?.length ? conditions : [{}]).map((condition: RuleBuilderCondition | {}) =>
        this.createConditionFormGroup(condition as RuleBuilderCondition)
      )
    );
    this.ruleForm.setControl('conditions', conditionArray);
  }

  private populateConditionsFromJson(condition: any): void {
    const normalized = this.extractConditionGroup(condition);
    if (!normalized) {
      this.ruleForm.patchValue({ logicalOperator: 'AND' }, { emitEvent: false });
      this.replaceConditions([]);
      return;
    }

    this.ruleForm.patchValue({ logicalOperator: normalized.logicalOperator }, { emitEvent: false });
    this.replaceConditions(normalized.rules);
  }

  private syncFieldOptionsForSelectedSection(): void {
    const panelKey = this.ruleForm.get('sectionKey')?.value;
    const selectedPanel = this.panelOptions.find(
      (panel: RuleBuilderPanelOption) => panel.sectionNameLabel === panelKey
    );

    this.fieldOptions = selectedPanel?.sectionFields || [];
    const availableFields = this.fieldOptions.map((field: RuleBuilderFieldOption) => field.fieldKey);

    this.conditionRows.controls.forEach((control: any) => {
      const fieldKey = String(control.get('fieldKey')?.value || '');
      if (fieldKey && availableFields.includes(fieldKey)) {
        return;
      }
      control.patchValue({ fieldKey: '' }, { emitEvent: false });
    });
  }

  private extractConditionGroup(condition: any): { logicalOperator: 'AND' | 'OR'; rules: RuleBuilderCondition[] } | null {
    if (!condition || typeof condition !== 'object') {
      return null;
    }

    if (condition.field && condition.operator) {
      return {
        logicalOperator: 'AND',
        rules: [{
          field: String(condition.field || ''),
          operator: String(condition.operator || 'equals'),
          value: condition.value
        }]
      };
    }

    if (!Array.isArray(condition.rules)) {
      return null;
    }

    const rules = condition.rules
      .filter((rule: any) => rule && typeof rule === 'object' && rule.field && rule.operator)
      .map((rule: any) => ({
        field: String(rule.field || ''),
        operator: String(rule.operator || 'equals'),
        value: rule.value
      }));

    if (!rules.length) {
      return null;
    }

    return {
      logicalOperator: String(condition.logicalOperator || 'AND').trim().toUpperCase() === 'OR' ? 'OR' : 'AND',
      rules
    };
  }
}
